import { useCallback, useEffect, useState } from "react";
import api from "../../../services/api";
import { useNotification } from "../../../context/NotificationContext";
import {
  FaCommentDots,
  FaTrash,
  FaEnvelopeOpen,
  FaReply,
  FaInbox,
  FaCheckCircle,
  FaTimes,
} from "react-icons/fa";

function MessagesAdmin() {
  const { showNotification, confirmAction } = useNotification();
  const [messages, setMessages] = useState([]);
  const [replyMessage, setReplyMessage] = useState("");
  const [selectedMessage, setSelectedMessage] = useState(null);

  const token = localStorage.getItem("token");

  const fetchMessages = useCallback(() => {
    api
      .get("/admin/messages", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setMessages(res.data))
      .catch(console.error);
  }, [token]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const markAsRead = async (id) => {
    try {
      await api.put(
        `/admin/messages/${id}`,
        { status: "Read" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      fetchMessages();
    } catch (error) {
      console.error(error);
      showNotification("עדכון ההודעה נכשל", "error");
    }
  };

  const deleteMessage = async (id) => {
    const confirmed = await confirmAction({
      title: "מחיקת הודעה",
      message: "האם למחוק את ההודעה? הפעולה אינה ניתנת לביטול.",
      confirmText: "מחיקה",
    });
    if (!confirmed) return;

    try {
      await api.delete(`/admin/messages/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchMessages();
      showNotification("ההודעה נמחקה בהצלחה", "success");
    } catch (error) {
      console.error(error);
      showNotification("מחיקת ההודעה נכשלה", "error");
    }
  };

  const sendReply = async () => {
    if (!replyMessage.trim()) {
      showNotification("יש לכתוב תגובה לפני השליחה", "warning");
      return;
    }

    try {
      await api.post(
        `/admin/messages/${selectedMessage.message_id}/reply`,
        {
          subject: `תגובה: ${selectedMessage.subject}`,
          message: replyMessage,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      showNotification("התגובה נשלחה בהצלחה", "success");
      setReplyMessage("");
      setSelectedMessage(null);
      fetchMessages();
    } catch (err) {
      console.log(err);
      showNotification("שליחת התגובה נכשלה", "error");
    }
  };

  const translateStatus = (status) => {
    const statuses = { Unread: "חדשה", Read: "נקראה", Answered: "נענתה" };
    return statuses[status] || status;
  };

  const unreadCount = messages.filter((m) => m.status === "Unread").length;
  const answeredCount = messages.filter((m) => m.status === "Answered").length;

  return (
    <section className="dashboard-section messages-section">
      <div className="section-title">
        <div className="section-title-main">
          <span className="section-icon"><FaCommentDots /></span>
          <div>
            <h2>הודעות ופניות</h2>
            <p>ניהול פניות שהתקבלו דרך טופס יצירת הקשר</p>
          </div>
        </div>
        {unreadCount > 0 && <span className="section-count attention">{unreadCount} חדשות</span>}
      </div>

      <div className="admin-mini-stats message-mini-stats">
        <div><span className="mini-stat-icon"><FaInbox /></span><strong>{messages.length}</strong><span>סה״כ פניות</span></div>
        <div className={unreadCount ? "warning" : ""}><span className="mini-stat-icon"><FaCommentDots /></span><strong>{unreadCount}</strong><span>ממתינות לקריאה</span></div>
        <div><span className="mini-stat-icon"><FaCheckCircle /></span><strong>{answeredCount}</strong><span>נענו</span></div>
      </div>

      <div className="message-list">
        {messages.length === 0 ? (
          <div className="empty-state-card"><FaInbox /><h3>אין הודעות חדשות</h3><p>פניות חדשות מהאתר יופיעו כאן.</p></div>
        ) : (
          messages.map((msg) => (
            <article className={`message-card ${msg.status === "Unread" ? "unread" : ""}`} key={msg.message_id}>
              <div className="message-header">
                <div className="message-sender">
                  <span className="message-avatar">{String(msg.name || "?").charAt(0)}</span>
                  <div>
                    <h3>{msg.name}</h3>
                    <small>{msg.email}</small>
                  </div>
                </div>

                <span className={`status ${String(msg.status).toLowerCase()}`}>
                  {translateStatus(msg.status)}
                </span>
              </div>

              <div className="message-body">
                <h4>{msg.subject || "ללא נושא"}</h4>
                <p>{msg.message}</p>
              </div>

              <div className="message-actions">
                <button onClick={() => markAsRead(msg.message_id)}>
                  <FaEnvelopeOpen /> סמן כנקראה
                </button>
                <button onClick={() => { setSelectedMessage(msg); setReplyMessage(""); }}>
                  <FaReply /> תגובה
                </button>
                <button className="danger" onClick={() => deleteMessage(msg.message_id)}>
                  <FaTrash /> מחיקה
                </button>
              </div>
            </article>
          ))
        )}
      </div>

      {selectedMessage && (
        <div className="reply-modal" role="dialog" aria-modal="true">
          <div className="reply-box">
            <div className="form-modal-header">
              <div>
                <span className="modal-eyebrow">CUSTOMER REPLY</span>
                <h3>תגובה אל {selectedMessage.name}</h3>
                <p>{selectedMessage.email}</p>
              </div>
              <button className="modal-close-btn" onClick={() => setSelectedMessage(null)} aria-label="סגירה"><FaTimes /></button>
            </div>

            <div className="reply-original-message">
              <span>הודעה מקורית</span>
              <strong>{selectedMessage.subject || "ללא נושא"}</strong>
              <p>{selectedMessage.message}</p>
            </div>

            <label className="form-field full">
              <span>תוכן התגובה</span>
              <textarea
                placeholder="כתבו כאן את התגובה ללקוח..."
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
              />
            </label>

            <div className="reply-buttons">
              <button onClick={sendReply}><FaReply /> שליחת תגובה</button>
              <button className="secondary" onClick={() => setSelectedMessage(null)}>ביטול</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default MessagesAdmin;
